import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { LoaderIcon } from '../icons';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2 fill-current">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const SignInModal = ({ isOpen, onClose, signInWithGoogle, signInWithApple }) => {
  const [loading, setLoading] = useState({ google: false, apple: false });
  const [error, setError] = useState('');

  const handleGoogleSignIn = async () => {
    console.log('🚨 Google sign-in button clicked');
    setLoading(prev => ({ ...prev, google: true }));
    setError('');
    
    try {
      // Store anonymous user ID before redirect (for potential migration)
      const anonymousUserId = localStorage.getItem('cf_user_id');
      if (anonymousUserId) {
        sessionStorage.setItem('migrate_from_user', anonymousUserId);
      }
      
      console.log('🚨 signInWithGoogle available:', !!signInWithGoogle);
      
      // Use the auth hook's method
      if (signInWithGoogle) {
        console.log('🚨 Using auth hook method');
        await signInWithGoogle();
      } else {
        console.log('🚨 Using fallback API call to /api/auth/google');
        // Fallback to direct API call
        const response = await fetch('/api/auth/google', {
          credentials: 'include'
        });
        
        console.log('🚨 API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('🚨 Redirecting to auth URL:', data.authUrl);
          window.location.href = data.authUrl;
        } else {
          const errorData = await response.text();
          console.error('🚨 API error response:', errorData);
          throw new Error(`Failed to initiate Google sign-in: ${errorData}`);
        }
      }
    } catch (error) {
      console.error('🚨 Google sign-in error:', error);
      setError(`Failed to start Google sign-in: ${error.message}`);
      setLoading(prev => ({ ...prev, google: false }));
    }
  };

  const handleAppleSignIn = async () => {
    setLoading(prev => ({ ...prev, apple: true }));
    setError('');
    
    try {
      // Store anonymous user ID before redirect (for potential migration)
      const anonymousUserId = localStorage.getItem('cf_user_id');
      if (anonymousUserId) {
        sessionStorage.setItem('migrate_from_user', anonymousUserId);
      }
      
      // Use the auth hook's method
      if (signInWithApple) {
        await signInWithApple();
      } else {
        // Fallback to direct API call
        const response = await fetch('/api/auth/apple', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.authUrl;
        } else {
          const errorData = await response.text();
          throw new Error(`Failed to initiate Apple sign-in: ${errorData}`);
        }
      }
    } catch (error) {
      console.error('Apple sign-in error:', error);
      setError(`Failed to start Apple sign-in: ${error.message}`);
      setLoading(prev => ({ ...prev, apple: false }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" open={isOpen} onClose={onClose}>
        <DialogHeader>
          <DialogTitle className="text-center">Sign In to Your Account</DialogTitle>
          <DialogDescription className="text-center">
            Sign in to save your favorite properties and collections across all your devices.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                {error}
                <button
                  onClick={() => setError('')}
                  className="ml-2 text-xs underline hover:no-underline"
                >
                  Dismiss
                </button>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-3">
            <Button
              onClick={handleGoogleSignIn}
              disabled={loading.google || loading.apple}
              variant="outline"
              className="w-full h-12 text-sm font-medium transition-opacity"
            >
              {loading.google ? (
                <>
                  <LoaderIcon className="mr-2 w-5 h-5 animate-spin" />
                  Connecting to Google...
                </>
              ) : (
                <>
                  <GoogleIcon />
                  Continue with Google
                </>
              )}
            </Button>
            
            <Button
              onClick={handleAppleSignIn}
              disabled={loading.google || loading.apple}
              variant="outline"
              className="w-full h-12 text-sm font-medium transition-opacity"
            >
              {loading.apple ? (
                <>
                  <LoaderIcon className="mr-2 w-5 h-5 animate-spin" />
                  Connecting to Apple...
                </>
              ) : (
                <>
                  <AppleIcon />
                  Continue with Apple
                </>
              )}
            </Button>
          </div>
          
          <div className="text-center text-sm text-muted-foreground space-y-2">
            <p>
              Don't want to sign in? You can continue browsing as a guest, 
              but your favorites and collections won't be saved.
            </p>
            <Button
              onClick={onClose}
              variant="ghost"
              className="text-xs h-8 px-4"
            >
              Continue as Guest
            </Button>
          </div>
        </div>
        
        <div className="text-xs text-muted-foreground text-center border-t border-border pt-4">
          By signing in, you agree to our terms of service and privacy policy.
          Your data is securely stored and never shared with third parties.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignInModal;